/**
 * Shared pixel rendering utility for DICOM thumbnail and preview generation.
 *
 * Extracted from FindingThumbnails so that useFindingSlices can render at
 * native-resolution (capped at 512px) while FindingThumbnails keeps its 160px
 * thumbnail behaviour. No annotation / bbox drawing — clean pixels only.
 */

export type PixelDataArray =
  | Int8Array
  | Int16Array
  | Int32Array
  | Uint8Array
  | Uint8ClampedArray
  | Uint16Array
  | Uint32Array
  | Float32Array
  | Float64Array;

/**
 * Render DICOM pixel data to an HTMLCanvasElement with windowing applied.
 *
 * @param pixelData  Raw pixel values (post–prescale, i.e. in HU for CT).
 * @param rows       Image height in pixels.
 * @param columns    Image width in pixels.
 * @param windowCenter  Centre of the display window (e.g. 40 for soft tissue).
 * @param windowWidth   Width of the display window (e.g. 400 for soft tissue).
 * @param outputSize  Edge length of the square output canvas (default 160).
 */
export function renderPixelsToCanvas(
  pixelData: PixelDataArray,
  rows: number,
  columns: number,
  windowCenter: number,
  windowWidth: number,
  outputSize = 160,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d")!;

  const lower = windowCenter - windowWidth / 2;
  const upper = windowCenter + windowWidth / 2;

  // Scale to output size while preserving aspect ratio
  const scale = Math.min(outputSize / columns, outputSize / rows);
  const sw = Math.round(columns * scale);
  const sh = Math.round(rows * scale);
  const ox = Math.round((outputSize - sw) / 2);
  const oy = Math.round((outputSize - sh) / 2);

  // Build full-resolution image data first, then scale down via drawImage
  const fullCanvas = document.createElement("canvas");
  fullCanvas.width = columns;
  fullCanvas.height = rows;
  const fullCtx = fullCanvas.getContext("2d")!;
  const imageData = fullCtx.createImageData(columns, rows);
  const data = imageData.data;

  for (let i = 0; i < pixelData.length; i++) {
    const val = pixelData[i] as number;
    const mapped = Math.max(0, Math.min(255, ((val - lower) / (upper - lower)) * 255));
    data[i * 4] = mapped;
    data[i * 4 + 1] = mapped;
    data[i * 4 + 2] = mapped;
    data[i * 4 + 3] = 255;
  }

  fullCtx.putImageData(imageData, 0, 0);
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, outputSize, outputSize);
  ctx.drawImage(fullCanvas, ox, oy, sw, sh);

  return canvas;
}
